import { Axios } from 'axios';
import apiClient from 'services/api/axios';
import {
  IAttachableNetworksDtoResponse,
  ICheckIsNetworkDto,
  IDnsDtoResponse,
  IFreeNetworkListDto,
  ILinkLocalNetworkDto, ILinkLocalNetworkResDtoRes,
  ILinkPublicNetworkDto,
  INetworkCreateDto, INetworkDtoResponse, INetworksByVmRes, INetworksDtoResponse,
  IPublicIpCreateDto,
  IPublicIpCreateDtoResponse,
  IPublicIpsDtoResponse,
  IRouterCreateDto,
  IRouterDtoResponse,
  IRouterHistoryDtoResponse,
  IRouterNetworkUpdateDto,
  IRoutersDtoResponse,
  IRouterUpdateDto,
  ITunnelCreateDto,
  ITunnelDtoResponse,
  ITunnelRoutesDtoResponse,
  ITunnelsDtoResponse,
  IUnlinkNetworkDto,
  IUsedIpsResponse,
} from 'store/network/types/api-bodies.types';
import { NetworkAddGatewayForm } from 'store/network/types/network';
import { RouterDatabasesDto } from 'components/Database/types/dtos';

class NetworkService {
  api: Axios;

  constructor(api: Axios) {
    this.api = api;
  }

  getNetworks() {
    return this.api.get<INetworksDtoResponse>('panel/netList');
  }

  getNetworksByVm(vmId: string) {
    return this.api.get<INetworksByVmRes>(`panel/getAllVmNetsByVm/${vmId}`);
  }

  createLocalNetwork(dto: INetworkCreateDto) {
    return this.api.post('panel/createLocalNet', dto);
  }

  createPublicNetwork(dto: INetworkCreateDto) {
    return this.api.post('panel/createPublicNet', dto);
  }

  linkLocalNetwork(vmId: string, dto: ILinkLocalNetworkDto) {
    return this.api.post<ILinkLocalNetworkResDtoRes>(`panel/vm/${vmId}/linkLocalNet`, dto);
  }

  unlinkLocalNetwork(vmId: string, dto: IUnlinkNetworkDto) {
    return this.api.post(`panel/vm/${vmId}/unlinkNet`, dto);
  }

  linkPublicNetwork(vmId: string, dto: ILinkPublicNetworkDto) {
    return this.api.post(`panel/vm/${vmId}/linkPublicNet`, dto);
  }

  deleteNetwork(netId: number) {
    return this.api.post(`panel/deleteNet/${netId}`);
  }

  addGateway(netId: number, dto: NetworkAddGatewayForm) {
    return this.api.post('panel/addGatewayToNet', dto, {
      params: {
        netId,
      },
    });
  }

  getFreeIpAddresses(networkId: number) {
    return this.api.get<IFreeNetworkListDto>(`panel/getFreeIpAddressesFromNet/${networkId}`);
  }

  checkIsNetwork(cidr: string, netId?: number) {
    return this.api.get<ICheckIsNetworkDto>('panel/getIsNetworkAddresses', {
      params: {
        address: cidr,
        userNetId: netId,
      },
    });
  }

  getNetwork(netId: number) {
    return this.api.get<INetworkDtoResponse>(`/panel/nets/${netId}`);
  }

  getUserDns() {
    return this.api.get<IDnsDtoResponse>('/panel/getUserDns');
  }

  addUserDns(dnsArray: string, allProjects = false) {
    return this.api.post(`panel/addUserDns?dnsArray=${dnsArray}&allProjects=${allProjects}`);
  }

  deleteUserDns(dnsId: number) {
    return this.api.delete(`panel/deleteUserDns/${dnsId}`);
  }

  renameNet(id: number, name: string) {
    return this.api.patch(`panel/renameNet?id=${id}&name=${name}`);
  }

  getUsedIps(id: number) {
    return this.api.get<IUsedIpsResponse>(`panel/getNetworkUsedIps/${id}`);
  }

  getAttachableNetworks(regionId: number) {
    return this.api.get<IAttachableNetworksDtoResponse>(`v2/routers/attachable-networks?regionId=${regionId}`);
  }

  /** region — код локации; без него бэк отдаёт IP текущей локации пользователя */
  getPublicIps(regionId?: string) {
    return this.api.get<IPublicIpsDtoResponse>('v2/public-ips', { params: { regionId } });
  }

  createPublicIp(dto: IPublicIpCreateDto) {
    return this.api.post<IPublicIpCreateDtoResponse>('v2/public-ips', dto);
  }

  getRouters() {
    return this.api.get<IRoutersDtoResponse>('v2/routers');
  }

  // projectTag передаётся явным параметром (а не только через заголовок X-Project-Tag),
  // чтобы загрузчик роутера не ушёл в бэкенд без проекта, пока стор ещё не гидратирован
  // (например, сразу после дублирования вкладки браузера)
  getRouter(id: number, projectTag?: string) {
    return this.api.get<IRouterDtoResponse>(`v2/routers/${id}`, { params: { projectTag } });
  }

  getRouterDatabases(id: number) {
    return this.api.get<RouterDatabasesDto>(`v2/routers/${id}/databases`);
  }

  getRouterHistory(id: number) {
    return this.api.get<IRouterHistoryDtoResponse>(`v2/routers/${id}/history`);
  }

  addRouter(dto: IRouterCreateDto) {
    return this.api.post('v2/routers', dto);
  }

  editRouter(id: number, dto: IRouterUpdateDto) {
    return this.api.patch(`v2/routers/${id}`, dto);
  }

  /** настройки одной подключённой сети; интернет отвечает за 5–15 с */
  editRouterNetwork(id: number, userNetId: number, dto: IRouterNetworkUpdateDto) {
    return this.api.patch(`v2/routers/${id}/networks/${userNetId}`, dto);
  }

  // deletePublicIp — галка «Удалить публичный IP адрес»; без неё IP остаётся за клиентом
  deleteRouter(id: number, deletePublicIp = false) {
    return this.api.delete(`v2/routers/${id}`, { params: { deletePublicIp } });
  }

  getTunnels() {
    return this.api.get<ITunnelsDtoResponse>('v2/tunnels');
  }

  getTunnelById(id: number) {
    return this.api.get<ITunnelDtoResponse>(`v2/tunnels/${id}`);
  }

  addTunnel(dto: ITunnelCreateDto) {
    return this.api.post('v2/tunnels', dto);
  }

  editTunnel(id: number, dto: ITunnelCreateDto) {
    return this.api.patch(`v2/tunnels/${id}`, dto);
  }

  getTunnelRoutes(id: number) {
    return this.api.get<ITunnelRoutesDtoResponse>(`v2/tunnels/${id}/routes`);
  }

  deleteTunnel(id: number) {
    return this.api.delete(`v2/tunnels/${id}`);
  }
}

export default new NetworkService(apiClient);
